import { Popconfirm, Tooltip } from "antd";
import {
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  Edit,
  Eye,
  Link2,
  MessageSquare,
  Package,
  Ticket,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useDeleteBooking } from "~/services/booking";
import { useBooking } from "~/store";
import { ContainerInfoList } from "~/components/common/container-info";

import { Typography } from "antd";
import { ContainerInfoCard } from "~/components/common/container-info";

const { Text } = Typography;

// Get payment status badge
const getPaymentBadge = (status: string) => {
  let bgColor = "";

  switch (status) {
    case "paid":
      bgColor = "bg-green-100 text-green-800";
      break;
    case "pending":
      bgColor = "bg-yellow-100 text-yellow-800";
      break;
    case "overdue":
      bgColor = "bg-red-100 text-red-800";
      break;
    case "deposit":
      bgColor = "bg-blue-100 text-blue-800";
      break;
    default:
      bgColor = "bg-gray-100 text-gray-800";
  }

  return (
    <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${bgColor}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Calculate dynamic booking status based on service tickets and actual status field
const calculateBookingStatus = (booking: any): string => {
  // PRIORITY 1: Use the actual booking status field if it exists
  // This ensures consistency with the detail view and backend status cascade logic
  if (booking.status) {
    // Special handling: if status is DRAFT but has service tickets, recalculate
    if (booking.status === "DRAFT" && booking.serviceTickets && booking.serviceTickets.length > 0) {
      // Continue to recalculation below
    } else {
      // Use the actual status for ESTIMATED, SCHEDULED, IN-PROGRESS, COMPLETED
      return booking.status;
    }
  }

  const serviceTickets = booking.serviceTickets || [];

  // If no service tickets, it's a DRAFT
  if (serviceTickets.length === 0) {
    return "DRAFT";
  }

  // Count statuses based on timeIn/timeOut timestamps for legacy support
  let completedCount = 0;
  let inProgressCount = 0;
  let scheduledCount = 0;

  serviceTickets.forEach((ticket: any) => {
    // Check if ticket has timeOut (completed)
    if (ticket.timeOut || ticket.completedAt || ticket.completed_at) {
      completedCount++;
    }
    // Check if ticket has timeIn but no timeOut (in progress)
    else if (ticket.timeIn && !ticket.timeOut) {
      inProgressCount++;
    }
    // Otherwise it's scheduled
    else {
      scheduledCount++;
    }
  });

  const totalTickets = serviceTickets.length;

  // All tickets completed = COMPLETED
  if (completedCount === totalTickets && totalTickets > 0) {
    return "COMPLETED";
  }

  // Some completed or any in progress = IN-PROGRESS
  if (completedCount > 0 || inProgressCount > 0) {
    return "IN-PROGRESS";
  }

  // Has scheduled tickets = SCHEDULED
  if (scheduledCount > 0) {
    return "SCHEDULED";
  }

  // Fallback to booking status or DRAFT
  return booking.status || "DRAFT";
};

// Get status badge for a booking
const getStatusBadge = (status: string) => {
  let bgColor = "";

  switch (status) {
    case "ESTIMATED":
      bgColor = "bg-yellow-100 text-yellow-800";
      break;
    case "SCHEDULED":
      bgColor = "bg-green-100 text-green-800";
      break;
    case "IN-PROGRESS":
      bgColor = "bg-purple-100 text-purple-800";
      break;
    case "COMPLETED":
      bgColor = "bg-blue-100 text-blue-800";
      break;
    case "DRAFT":
      bgColor = "bg-gray-100 text-gray-800";
      break;
    // BookingStatus enum values
    case "PENDING":
      bgColor = "bg-gray-100 text-gray-800";
      break;
    case "CONFIRMED":
      bgColor = "bg-green-100 text-green-800";
      break;
    case "CANCELED":
    case "CANCELLED":
      bgColor = "bg-red-100 text-red-800";
      break;
    default:
      bgColor = "bg-gray-100 text-gray-800";
  }

  return (
    <span className={`px-2 py-0.5 text-xs font-bold uppercase tracking-wide ${bgColor}`}>
      {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace("-", " ")}
    </span>
  );
};

export const BookingItem = ({
  booking,
}: {
  booking: {
    id: string;
    referenceNumber: string;
    client: string;
    isQuickClient?: boolean;
    isAppBooking?: boolean;
    project: string;
    sidemark?: string;
    date: string;
    status: any;
    paymentStatus: string;
    totalAmount: number;
    hasInvoice?: boolean;
    type: any;
    description: string;
    inventory: {
      id: string;
      title: string;
    }[];
    networkConnectType?: "shuttle" | "network" | "vendor" | null;
    originalBooking?: any;
    serviceTickets?: any[];
  };
}) => {
  const [open, setOpen] = useState(false);
  const [showContainerInfo, setShowContainerInfo] = useState(false);

  const { openBookingModal } = useBooking();

  const { mutate: deleteBooking, isPending: isLoadingDelete } =
    useDeleteBooking();

  const showPopconfirm = () => {
    setOpen(true);
  };

  const handleOk = () => {
    deleteBooking(booking.id, {
      onSuccess: () => {
        setOpen(false);
      },
      onError: () => {
        setOpen(false);
      },
    });
  };

  const handleCancel = () => {
    console.log("Clicked cancel button");
    setOpen(false);
  };

  // Calculate dynamic status
  const displayStatus = calculateBookingStatus(booking);

  return (
    <div key={booking.id} className="p-4 hover:bg-[#fafafa] transition-colors">
      <div className="grid grid-cols-12 gap-4 items-center text-sm text-[#1a1a2e]">
        <div className="col-span-2 font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{booking.referenceNumber}</div>
        <div className="col-span-3 truncate">
          {booking.client}
          {booking.isQuickClient && (
            <span className="ml-1 text-xs font-bold uppercase tracking-wide bg-orange-100 text-orange-700 px-1.5 py-0.5">New Lead</span>
          )}
          {booking.isAppBooking && (
            <span className="ml-1 text-xs font-bold uppercase tracking-wide bg-orange-100 text-orange-700 px-1.5 py-0.5">APP</span>
          )}
          {booking.networkConnectType && (
            <span className="ml-1 text-xs font-bold uppercase tracking-wide bg-[#1a1a2e] text-white px-1.5 py-0.5 inline-flex items-center gap-1">
              <Link2 size={10} />
              {booking.networkConnectType === "shuttle" ? "Shuttle" : booking.networkConnectType === "network" ? "Network" : "Vendor"}
            </span>
          )}
        </div>
        <div className="col-span-2">
          <div className="truncate font-medium">{booking.project || "-"}</div>
          {booking.sidemark && (
            <div className="text-xs text-[#999] truncate">
              SM: <span className="font-mono">{booking.sidemark}</span>
            </div>
          )}
        </div>
        <div className="col-span-1 text-[#999]">{booking.date}</div>
        <div className="col-span-1">{getStatusBadge(displayStatus)}</div>
        <div className="col-span-1 flex items-center font-medium">
          <span>${booking.totalAmount.toFixed(2)}</span>
          {booking.hasInvoice && (
            <Tooltip title="Invoiced">
              <Check size={14} className="ml-1 text-green-600" />
            </Tooltip>
          )}
        </div>
        <div className="col-span-2 flex space-x-1">
          <Link to={`/bookings/${booking.id}`}>
            <button
              className="cursor-pointer p-1.5 text-[#1a1a2e] hover:bg-[#f5f5f5] transition-colors"
              title="View Details"
            >
              <Eye size={16} />
            </button>
          </Link>

          <Link to={`/bookings/${booking.id}?tab=communication`}>
            <button
              className="p-1.5 text-[#1a1a2e] hover:bg-[#f5f5f5] transition-colors"
              title="Send Message"
            >
              <MessageSquare size={16} />
            </button>
          </Link>
          <Link to={`/bookings/copy/${booking.id}`}>
            <button
              className="p-1.5 text-[#1a1a2e] hover:bg-[#f5f5f5] cursor-copy transition-colors"
              title="Copy Booking"
            >
              <Copy size={16} />
            </button>
          </Link>
          <Popconfirm
            open={open}
            title="Delete Booking"
            description="Are you sure to delete this booking?"
            onConfirm={handleOk}
            okButtonProps={{ loading: isLoadingDelete }}
            onCancel={handleCancel}
            okText="Yes"
            cancelText="No"
          >
            <button
              className="p-1.5 text-red-600 hover:bg-red-50 transition-colors"
              title="Cancel Booking"
              onClick={showPopconfirm}
            >
              <Trash2 size={16} />
            </button>
          </Popconfirm>
        </div>
      </div>

      {/* Extra Booking Info */}
      <div className="mt-2 pl-2 text-sm text-[#666]">
        <div className="flex items-center space-x-2">
          <Ticket size={14} className="text-[#999]" />
          <span className="font-medium">Services:</span>
          <span>{booking.description}</span>
        </div>
        {booking.inventory && booking.inventory.length > 0 && (
          <div className="mt-1 flex items-center space-x-1">
            <Package size={14} className="text-[#999]" />
            <span>
              <span className="font-medium">
                {booking.inventory.length} items:
              </span>
              {booking.inventory.length <= 10 ? (
                <span className="ml-1 text-[#666]">
                  {booking.inventory.map((item) => item.title).join(", ")}
                </span>
              ) : (
                <Tooltip
                  title={
                    <div className="max-h-60 overflow-y-auto">
                      <ul className="list-disc pl-4">
                        {booking.inventory.map((item) => (
                          <li key={item.id}>{item.title}</li>
                        ))}
                      </ul>
                    </div>
                  }
                  placement="bottomLeft"
                  color="#fff"
                  overlayInnerStyle={{ color: "#333", maxWidth: "400px" }}
                >
                  <span className="ml-1 text-[#666] cursor-pointer hover:text-[#1a1a2e]">
                    {booking.inventory
                      .slice(0, 10)
                      .map((item) => item.title)
                      .join(", ")}
                    ...
                    <span className="text-xs text-[#999] ml-1">
                      (hover to see all)
                    </span>
                  </span>
                </Tooltip>
              )}
            </span>
          </div>
        )}

        {/* Container Information */}
        {booking.serviceTickets && booking.serviceTickets.length > 0 && (
          <div className="mt-3">
            <button
              onClick={() => setShowContainerInfo(!showContainerInfo)}
              className="flex items-center space-x-2 text-sm font-medium text-[#1a1a2e] hover:opacity-70 focus:outline-none px-2 py-1 transition-opacity"
            >
              {showContainerInfo ? (
                <ChevronDown size={14} className="text-[#999]" />
              ) : (
                <ChevronRight size={14} className="text-[#999]" />
              )}
              <span>Container Information ({booking.serviceTickets.length})</span>
            </button>
            {showContainerInfo && (
              <div className="mt-2 transition-all duration-200 ease-in-out">
                <ContainerInfoList
                  serviceTickets={booking.serviceTickets}
                  bookingId={booking.id}
                  tempClientData={booking.originalBooking?.tempClientData}
                  className="border-t pt-2"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
